import {
  ClaimResolutionMethodEnum,
  CrewClaimResolutionMethodEnum,
  Pegasus,
} from 'corso-types';
import { useMemo } from 'react';
import { CUSTOM_PARCEL_NAME } from '~/features/settings/automations/eventParams/predefinedParcels';
import { buildDiscountMetaDescription } from '~/features/settings/discountDefinitions/discountSummary';
import { useDiscountDefinitions } from '~/features/settings/hooks/useDiscountDefinitions';
import { useStoreUser } from '~/providers/StoreUserProvider';
import { shopNowBenefits } from '~/utils/enumNameMaps';

const useEventRegistrationDiscountDefaults = () => {
  const { store } = useStoreUser();
  const { data: discountDefinitions } = useDiscountDefinitions();

  const [firstDiscount] = discountDefinitions;

  const discountDef = useMemo(
    () =>
      firstDiscount
        ? buildDiscountMetaDescription(firstDiscount, store.currencyCode)
        : null,
    [firstDiscount, store.currencyCode],
  );

  return useMemo(
    () => ({
      storeDiscountDefinitionId: firstDiscount?.id ?? 0,
      discountRedirectUrl: store.url ?? '',
      discountCustomerMessage: discountDef
        ? `Thank you for registering, please enjoy ${discountDef} your next order.`
        : '',
    }),
    [discountDef, firstDiscount?.id, store.url],
  );
};

export const useEventDefaultParams = () => {
  const regDiscountDefaults = useEventRegistrationDiscountDefaults();

  return useMemo(
    () =>
      ({
        [Pegasus.EventType.shopNow]: () => ({
          shopNowDescription: '',
          shopNowHeadline: '',
          startShoppingUrl: '',
          shopNowBenefits,
        }),
        [Pegasus.EventType.merchantPaidShippingProtection]: () => ({
          shipProtectUnitId: 0,
        }),
        [Pegasus.EventType.offerAdvancedExchange]: () => ({
          storeProductGroupIds: [],
          storeProductTags: [],
          advancedExchangeSortCriteria: 'ALPHABETICAL',
          exchangeKind: 'product',
          advancedExchangeMethod:
            Pegasus.AdvancedExchangeMethod.NO_PAYMENT_OR_REFUND,
          isAdvancedExchangeFilterAndSearchEnabled: true,
          excludedAdvancedExchangeOptionNames: ['Title'],
        }),
        [Pegasus.EventType.quoteOrderShipmentParcel]: () => ({
          parcel: {
            length: 10,
            width: 10,
            height: 10,
            name: CUSTOM_PARCEL_NAME,
          },
        }),
        [Pegasus.EventType.productRegistrationSelection]: () => ({
          productRegistrationProductGroupId: 0,
        }),
        [Pegasus.EventType.modifyResolutionWindow]: () => ({
          kind: 'eligibilityDays',
          refund: null,
          giftCard: null,
          exchange: null,
          warrantyReview: null,
          isIneligibleWarrantyClaimAllowed: false,
        }),
        [Pegasus.EventType.autoFinalizeClaim]: () => ({
          allowedResolutionMethods: [
            CrewClaimResolutionMethodEnum.refund,
            CrewClaimResolutionMethodEnum.giftCard,
            CrewClaimResolutionMethodEnum.variantExchange,
          ],
        }),
        [Pegasus.EventType.omitResolutionMethods]: () => ({
          omittedResolutionMethods: [],
        }),
        [Pegasus.EventType.askAPolicyEnforcingQuestion]: () => ({
          question: '',
          no: null,
          yes: 'markAsIneligible',
        }),
        [Pegasus.EventType.offerInstantExchange]: () => ({
          timeToReturnBeforeCharging: 7,
          isInstantExchangeRequired: false,
        }),
        [Pegasus.EventType.allowClaimTypeSelection]: () => ({
          claimTypeSelectionDetailText: '',
          returnSelectionButtonTitle: 'Return',
        }),
        [Pegasus.EventType.collectCustomFields]: () => ({
          showDefaultCommentField: true,
          isMediaUploadRequired: false,
          mediaUploadInstructions: '',
          minimumMediaUploadAmount: 1,
          isDefaultCommentFieldRequired: false,
          customFieldIds: [],
          allowedContentTypes: ['image', 'video', 'pdf'],
        }),
        [Pegasus.EventType.applyClaimTags]: () => ({
          claimTags: [],
        }),
        [Pegasus.EventType.applyOrderTags]: () => ({
          orderTags: [],
          orderTagAction: 'Original Order',
        }),
        [Pegasus.EventType.customizeClaimFinalizationSettings]: () => ({
          restockToShopify: false,
          failOnOutOfStockScenarios: false,
          failureToFinalizeNotificationEmails: '',
        }),
        [Pegasus.EventType.modifyIncentives]: () => ({
          giftCardIncentive: { shouldModify: false },
          shouldApplyVariantExchangeShipping: false,
        }),
        [Pegasus.EventType.applyFee]: () => ({
          feeAmount: 0,
          feeDisplayName: '',
          feeType: 'Fixed',
        }),
        [Pegasus.EventType.applyLineItemFee]: () => ({
          feeAmount: 0,
          feeDisplayName: '',
          feeType: 'Fixed',
          applyPerItem: false,
        }),
        [Pegasus.EventType.chargeForReturnLabel]: () => ({
          applyLabelMarkup: { shouldModify: false },
        }),
        [Pegasus.EventType.chargeForExchangeOrderShipping]: () => ({
          applyExchangeOrderShippingMarkup: { shouldModify: false },
        }),
        [Pegasus.EventType.registrationDiscountIncentive]: () => ({
          ...regDiscountDefaults,
        }),
        [Pegasus.EventType.allowCustomerToKeepItem]: () => ({}),
        [Pegasus.EventType.returnShippingConfig]: () => ({
          returnHandling: Pegasus.ReturnHandling.returnLocation,
          returnLocationId: 0,
          returnMethods: [],
        }),
        [Pegasus.EventType.customizeClaimLineItemInspection]: () => ({
          inspectionCustomFieldIds: [],
          shouldCollectInspectionGrade: false,
          showInspectionComment: false,
          showInspectionMediaUpload: false,
          inspectionInstructions: '',
        }),
        [Pegasus.EventType.quoteOrderShipment]: () => ({
          storeShipRateIds: [],
          freeShippingOptInRateId: [],
        }),
        [Pegasus.EventType.quoteOrderShipmentModify]: () => ({
          checkoutRateModifications: [],
        }),
        [Pegasus.EventType.quoteOrderShipmentDeliveryEstimate]: () => ({
          deliveryEstimateConfigs: [],
        }),
        [Pegasus.EventType.orderEditing]: () => ({
          editType: 'CANCEL_ORDER',
          shouldNotifyOnCancellation: false,
          shouldRestockOnCancellation: false,
        }),
        [Pegasus.EventType.restockInventory]: () => ({
          restockPlatform: 'SHOPIFY',
          shopifyRestockName: 'available',
          restockLocationIdFromPlatform: '',
        }),
        [Pegasus.EventType.customizeReturnShipment]: () => ({
          isApplicablePerLineItemQuantity: true,
          returnParcels: [
            {
              length: 10,
              width: 10,
              height: 10,
              name: CUSTOM_PARCEL_NAME,
              weight: 1,
            },
          ],
        }),
        [Pegasus.EventType.createGorgiasTicket]: () => ({
          subject: '{{claimType}} {{claimName}} Created',
          senderEmail: '',
          tags: [],
        }),
        [Pegasus.EventType.setOrderShippingTitle]: () => ({
          shippingTitle: '',
        }),
        [Pegasus.EventType.rightToWithdraw]: () => ({
          rightToWithdrawTitle: 'Right To Withdraw',
          rightToWithdrawDescription:
            'You have the right to return your order within 14 days, for any reason and without a justification.',
        }),
        [Pegasus.EventType.shipClaimAllowedResolutions]: () => ({
          shippingClaimResolutionMethods: [ClaimResolutionMethodEnum.refund],
          isGiftCardEnabled: false,
        }),
        [Pegasus.EventType.shipClaimReorderShipping]: () => ({
          shippingTitleKind: 'selection',
          shippingTitles: [],
        }),
        [Pegasus.EventType.shipClaimReorderLocations]: () => ({
          reorderLocationIds: [],
        }),
        [Pegasus.EventType.shipClaimSlaAlert]: () => ({
          alert: {
            variant: 'info',
            title: '',
            message: '',
          },
        }),
        [Pegasus.EventType.shipApplyOrderTags]: () => ({
          orderTags: [],
          orderTagAction: 'Original Order',
        }),
      }) as const satisfies {
        [E in Pegasus.EventType]: () => Pegasus.EventParams<E>;
      },
    [regDiscountDefaults],
  );
};
